import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

import { Store } from "../../../types/Store";
import StoresFragment from "./components/storeCustomers/CustomersFragment";
import { Community } from "../../../types/Community";
import getUsersFromCommunity from "../../../database/customers/getUsersFromCommunity";
import Customer from "../../../types/Customer";
import getAllHabits from "../../../database/habits/getAllHabits";
import CustomerEngagementTab from "./components/CustomerEngagementTab";

import {
  DesignSystemSurface,
  PageHeader,
} from "../../../design-system";
import {
  IconChart,
  IconUsers,
} from "../../../design-system/icons/icons";

const Page = styled.div`
  min-height: calc(100vh - 104px);
  padding: var(--space-6) var(--space-6) var(--space-16);
`;

const PageInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const TabBar = styled.div`
  display: inline-flex;
  gap: 2px;
  padding: 4px;
  margin-bottom: var(--space-6);
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
`;

const TabButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  border: 0;
  background: transparent;
  color: var(--color-text-secondary);
  border-radius: var(--radius-sm);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition:
    background var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard),
    transform var(--duration-fast) var(--ease-standard);

  &:hover {
    color: var(--color-text);
  }

  &:active {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  ${(props) =>
    props.$active &&
    css`
      background: var(--color-surface);
      color: var(--color-text);
      font-weight: var(--font-weight-semibold);
      box-shadow: var(--shadow-xs);
    `}
`;

type Props = {
  store: Store;
  isMobile: boolean;
  user: Customer;
  community: Community;
};

const Customers = (props: Props) => {
  const { isMobile, user, community } = props;
  const [currentTab, setCurrentTab] = useState<"directory" | "engagement">(
    "directory"
  );
  const { habits } = getAllHabits({ communityId: user?.communityId });

  const {
    users,
    loading: customersLoading,
    removeCustomer,
    updateCustomer,
    createCustomer,
    setCustomerFreeAccess,
  } = getUsersFromCommunity(user?.communityId);

  useEffect(() => {
    if (!("Notification" in window)) {
      console.log("This browser does not support desktop notification");
    } else {
      Notification.requestPermission();
    }
  }, []);

  const handleEditCustomer = (customer: Customer) => {
    const { id, ...updatedData } = customer;
    updateCustomer(id, updatedData);
  };

  return (
    <DesignSystemSurface>
      <Page>
        <PageInner>
          <PageHeader
            title="Customers"
            subtitle="Manage members and understand how they use your plans."
          />
          <TabBar role="tablist" aria-label="Customer views">
            <TabButton
              type="button"
              role="tab"
              $active={currentTab === "directory"}
              aria-selected={currentTab === "directory"}
              aria-controls="customer-directory-panel"
              onClick={() => setCurrentTab("directory")}
            >
              <IconUsers size={14} />
              Customers
            </TabButton>
            <TabButton
              type="button"
              role="tab"
              $active={currentTab === "engagement"}
              aria-selected={currentTab === "engagement"}
              aria-controls="customer-engagement-panel"
              onClick={() => setCurrentTab("engagement")}
            >
              <IconChart size={14} />
              Engagement
            </TabButton>
          </TabBar>

          {currentTab === "directory" ? (
            <div id="customer-directory-panel" role="tabpanel">
              <StoresFragment
                removeCustomer={removeCustomer}
                onEditCustomer={handleEditCustomer}
                onCreateCustomer={createCustomer}
                onSetFreeAccess={setCustomerFreeAccess}
                customers={users}
                isMobile={isMobile}
                actions={habits}
              />
            </div>
          ) : (
            <div id="customer-engagement-panel" role="tabpanel">
              <CustomerEngagementTab
                communityId={user?.communityId}
                customerCount={users.length}
                customersLoading={customersLoading}
              />
            </div>
          )}
        </PageInner>
      </Page>
    </DesignSystemSurface>
  );
};

const mapStateToProps = (state) => ({
  user: state.storeUser,
  isMobile: state.isMobile,
  community: state.community,
  adminConfig: state.adminConfig,
});

const mapDispatchToProps = (_dispatch) => {
  return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(Customers);
